import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { Recalls } from '@principle-theorem/principle-core';
import {
  BrandRecallField,
  IBrand,
  IPatient,
  IRecallSettings,
  RecallTrack,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  shareReplayCold,
  WithRef,
} from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { RecallSettingsComponent } from '../recall-settings/recall-settings.component';

export interface IBrandRecallSettingsDialogRequest {
  brand: WithRef<IBrand>;
  track?: RecallTrack;
}

export interface IBrandRecallSettingsDialogResponse {
  /**
   * Resolved recall settings for the chosen track. Optional because the
   * disable response (`action: 'disable'`) carries no settings payload.
   */
  settings?: IRecallSettings;
  updateForm: IBrandRecallUpdateFormData;
  updatePatients?: DocumentReference<IPatient>[];
  /**
   * Emitted when the user clicks the disable action on a
   * Secondary-track instance.
   */
  action?: 'disable';
}

export interface IBrandRecallUpdateFormData {
  addMissing: boolean;
  updateExisting: boolean;
  overrideExisting: boolean;
}

interface IPatientRecallUpdateCount {
  usingDefaultPeriod: number;
  withoutNextDueDate: number;
  withAutoNextDueDate: number;
  withExplicitNextDueDate: number;
}

@Component({
  selector: 'pr-brand-recall-settings-dialog',
  templateUrl: './brand-recall-settings-dialog.component.html',
  styleUrls: ['./brand-recall-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    RecallSettingsComponent,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
  ],
})
export class BrandRecallSettingsDialogComponent {
  private _dialogRef =
    inject<
      MatDialogRef<
        BrandRecallSettingsDialogComponent,
        IBrandRecallSettingsDialogResponse
      >
    >(MatDialogRef);

  request = inject<IBrandRecallSettingsDialogRequest>(MAT_DIALOG_DATA);
  readonly RecallTrack = RecallTrack;
  track: RecallTrack;
  title: string;
  settingsField: BrandRecallField;
  otherTrack: RecallTrack;
  otherTrackField: BrandRecallField;
  updateFormGroup = new TypedFormGroup<IBrandRecallUpdateFormData>({
    addMissing: new TypedFormControl<boolean>(false),
    updateExisting: new TypedFormControl<boolean>(false),
    overrideExisting: new TypedFormControl<boolean>(false),
  });
  updatedSettings = signal<IRecallSettings | undefined>(undefined);
  hasUpdateToApply: Signal<boolean>;
  canSubmit: Signal<boolean>;

  loadingCounts = signal(true);
  counts$: Observable<IPatientRecallUpdateCount>;

  constructor() {
    this.track = this.request.track ?? RecallTrack.Primary;
    this.title = `Edit ${Recalls.trackLabel(
      this.request.brand,
      this.track
    )} Settings`;
    this.settingsField = this._settingsField(this.track);
    this.otherTrack =
      this.track === RecallTrack.Primary
        ? RecallTrack.Secondary
        : RecallTrack.Primary;
    this.otherTrackField = this._settingsField(this.otherTrack);

    const updateFormChanges = toSignal(
      validFormGroupChanges$(this.updateFormGroup)
    );
    this.hasUpdateToApply = computed(() => {
      const value = updateFormChanges();
      return (
        !!value &&
        (value.addMissing || value.updateExisting || value.overrideExisting)
      );
    });
    this.canSubmit = computed(
      () =>
        !this.loadingCounts() &&
        (!!this.updatedSettings() || this.hasUpdateToApply())
    );

    this.counts$ = of(this.request.brand.ref).pipe(
      tap(() => this.loadingCounts.set(true)),
      switchMap((brandRef) => this._getPatientRecallUpdate(brandRef)),
      tap((results) => this._syncUpdateFormValidators(results)),
      tap(() => this.loadingCounts.set(false)),
      shareReplayCold()
    );
  }

  submit(): void {
    const settings =
      this.updatedSettings() ??
      this.request.brand.settings?.[this.settingsField];
    this._dialogRef.close({
      settings,
      updateForm: this.updateFormGroup.value,
    });
  }

  disableSecondary(): void {
    this._dialogRef.close({
      action: 'disable',
      updateForm: this.updateFormGroup.value,
    });
  }

  trackLabel(track: RecallTrack): string {
    return Recalls.trackLabel(this.request.brand, track);
  }

  private _settingsField(track: RecallTrack): BrandRecallField {
    return track === RecallTrack.Primary ? 'recalls' : 'secondaryRecalls';
  }

  private async _getPatientRecallUpdate(
    brandRef: DocumentReference<IBrand>
  ): Promise<IPatientRecallUpdateCount> {
    const usingDefaultPeriod = await Firestore.getCount(
      Recalls.patientsWithDefaultPeriodQuery(brandRef, this.track)
    );
    const withoutNextDueDate = await Firestore.getCount(
      Recalls.patientsWithoutRecallDueDateQuery(brandRef, this.track)
    );
    const withAutoNextDueDate = await Firestore.getCount(
      Recalls.patientsWithAutoRecallDueDateQuery(brandRef, this.track)
    );
    const withExplicitNextDueDate = await Firestore.getCount(
      Recalls.patientsWithExplicitRecallDueDateQuery(brandRef, this.track)
    );
    return {
      usingDefaultPeriod,
      withoutNextDueDate,
      withAutoNextDueDate,
      withExplicitNextDueDate,
    };
  }

  private _syncUpdateFormValidators(results: IPatientRecallUpdateCount): void {
    if (results.withoutNextDueDate) {
      this.updateFormGroup.controls.addMissing.enable();
    } else {
      this.updateFormGroup.controls.addMissing.disable();
    }

    if (results.withAutoNextDueDate) {
      this.updateFormGroup.controls.updateExisting.enable();
    } else {
      this.updateFormGroup.controls.updateExisting.disable();
    }

    if (results.withExplicitNextDueDate) {
      this.updateFormGroup.controls.overrideExisting.enable();
    } else {
      this.updateFormGroup.controls.overrideExisting.disable();
    }
  }
}
